import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Case, Switch, When } from 'react-if'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { GiftBowIcon } from '@/atoms/Icons/GiftBowIcon'
import { GiftIcon } from '@/atoms/Icons/GiftIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { TitleXXS } from '@/atoms/Text'
import { useSaleBanner } from '@/services/ContentfulSaleService/hooks'
import { useSafeTrack } from '@/utils/analytics'
import { useCountDown } from '@/utils/countdown/useCountdownHook'
import { getObjectFromLocalStorage, SALE_BANNER_DISMISSED, writeToLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

interface DismissedSales {
  [key: string]: boolean
}

interface SaleBannerProps {
  position?: 'top'
}

const GUILD_FUNNEL_PATH_REGEXES = [/\/guild.*/]

export const SaleBanner: React.FC<SaleBannerProps> = ({ position }) => {
  const track = useSafeTrack()
  const { saleWithBanner, loading } = useSaleBanner()
  const router = useSafeRouter()
  const [isDismissed, setIsDismissed] = useState(false)
  const isGuildFunnelPath = GUILD_FUNNEL_PATH_REGEXES.some((r) => r.test(router.asPath))
  const isTop = position === 'top'

  const saleId = saleWithBanner?.id ?? ''

  useEffect(() => {
    if (!saleId) {
      setIsDismissed(false)
      return
    }

    const dismissedSales = getObjectFromLocalStorage<DismissedSales>(SALE_BANNER_DISMISSED) ?? {}
    setIsDismissed(Boolean(dismissedSales[saleId]))
  }, [saleId])

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      track('Sale Banner Clicked', { saleWithBanner })

      const planSelectionElement = document.getElementById('plan-selection')

      // If plan-selection exists, scroll to it, otherwise, let the href handle navigation
      if (planSelectionElement) {
        e.preventDefault()
        planSelectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    [track, saleWithBanner],
  )

  const handleCloseBanner = useCallback(() => {
    if (!saleId) return

    track('Sale Banner Dismissed', { saleId, saleWithBanner })

    const dismissedSales = getObjectFromLocalStorage<DismissedSales>(SALE_BANNER_DISMISSED) ?? {}

    if (!dismissedSales[saleId]) {
      dismissedSales[saleId] = true
      writeToLocalStorage(SALE_BANNER_DISMISSED, dismissedSales)
    }

    setIsDismissed(true)
  }, [saleId, saleWithBanner, track])

  const isExpired = useMemo(() => {
    if (!saleWithBanner?.endDate) return false
    const endDate = new Date(saleWithBanner.endDate)
    return Number.isFinite(endDate.valueOf()) && endDate < new Date()
  }, [saleWithBanner?.endDate])

  if (!saleWithBanner || isDismissed || !saleWithBanner.saleBannerUrl || isExpired || loading) return null

  const color = 'copper'

  return (
    <div className={classNames('w-full', isTop ? 'relative h-11 z-40' : 'absolute top-[62px] z-40 md:top-16')}>
      <div className="relative w-full">
        <div className="absolute h-11 w-full bg-black opacity-70 shadow-sm"></div>
        <div className={'absolute flex h-11 w-full flex-row items-center justify-between '}>
          <div className="hidden w-11 md:block">
            {/* This div is here to keep the space on left and right equal so the middle content is centered on desktop. */}
          </div>
          <div className="grow-1">
            <div className="m-auto w-[90%] md:w-[80%]">
              <InternalLink href={saleWithBanner?.saleBannerUrl} onClick={handleClick}>
                <div className="flex h-full w-full flex-row items-center justify-center gap-1 p-3">
                  <Switch>
                    <Case condition={saleWithBanner?.saleBannerIcon === 'gift'}>
                      <GiftIcon className="shrink-0" size={20} color={color} />
                    </Case>
                    <Case condition={saleWithBanner?.saleBannerIcon === 'giftBow'}>
                      <GiftBowIcon className="shrink-0" size={20} color={color} />
                    </Case>
                  </Switch>
                  <TitleXXS className="line-clamp-2" color={color} weight="semibold">
                    {saleWithBanner.saleBannerText}
                    {!!saleWithBanner.showSaleBannerCountdown && (
                      <>
                        {' '}
                        <Countdown className="hidden md:inline" endDate={saleWithBanner?.endDate} />
                      </>
                    )}
                  </TitleXXS>
                </div>
              </InternalLink>
            </div>
          </div>
          <When condition={!isGuildFunnelPath}>
            <div className="h-full w-11">
              <Button className="h-full w-full" variant="plain" onClick={handleCloseBanner}>
                <CloseIcon size={20} color={color} />
              </Button>
            </div>
          </When>
        </div>
      </div>
    </div>
  )
}

const Countdown: React.FC<{ className: string; endDate: string | undefined }> = ({ className, endDate }) => {
  const format = useMemo(
    () => ({
      listStyle: 'narrow' as const,
      unitDisplay: 'long' as const,
    }),
    [],
  )

  const countdown = useCountDown({
    endDate: endDate,
    format,
    numUnits: 5,
  })

  return <span className={classNames('text-nowrap', className)}>{`— ${countdown?.formattedCountdown}`}</span>
}
